// Cookie consent management for analytics (GA4, PostHog)
// Essential cookies (Supabase auth) don't require consent

// Re-use the global Window augmentation from utils/analytics.ts
import type {} from '../utils/analytics';

const CONSENT_KEY = 'cookie-consent';

export type ConsentStatus = 'accepted' | 'rejected' | null;

/** Get stored consent status. Returns null if user hasn't decided yet. */
export function getConsentStatus(): ConsentStatus {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    if (value === 'accepted' || value === 'rejected') return value;
    return null;
  } catch {
    return null;
  }
}

/** Persist the user's consent choice. */
export function setConsentStatus(status: 'accepted' | 'rejected'): void {
  try {
    localStorage.setItem(CONSENT_KEY, status);
  } catch {
    // localStorage unavailable — consent defaults to rejected behavior
  }
}

/** Returns true if the user has explicitly accepted analytics cookies. */
export function hasAnalyticsConsent(): boolean {
  return getConsentStatus() === 'accepted';
}

/**
 * Load GA4 dynamically after consent is granted.
 * Mirrors the previous inline script from index.html.
 */
export function loadGA4(): void {
  const id = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
  if (!id) return;

  // Don't load twice — gtag may not exist on window yet despite the global type declaration
  if ((window as unknown as Record<string, unknown>).gtag) return;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', id, {
    page_title: document.title,
    page_location: window.location.href,
  });
}

/**
 * Revoke GA4 cookies and disable tracking when consent is rejected.
 * Clears _ga* cookies set by Google Analytics.
 */
export function revokeGA4(): void {

  // Disable gtag
  const id = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
  if (id && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
    });
  }

  // Clear GA cookies
  document.cookie.split(';').forEach((c) => {
    const name = c.trim().split('=')[0];
    if (name.startsWith('_ga')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  });
}
